import { createDictionaryFromList } from '@protector/utils/array';
import { isEmpty } from 'lodash';
import type { EntityAdapter } from 'redux-ngrx-entity';
import { createEntityAdapter } from 'redux-ngrx-entity';
import { CoreActionsTypes } from '../../core/__legacy__/redux.constants.ts';
import { PropertyActionsTypes } from '../../property/__legacy__/redux.constants.ts';
import { AreaVariableMassiveStages } from '../constants.ts';
import type { Region } from '../models.ts';
import {
  getUpdatedChildRegionsWithIndicatorPressure,
  getUpdatedParentRegionsWithIndicatorPressure,
  mapRegionToRegionUpdate,
  selectRegionUuid,
  setSeasonMethodologies,
  verifyPressureIndicator
} from '../utils.ts';
import { RegionActionsTypes } from './redux.actions.ts';
import type { ActionsResponses, RegionState } from './redux.types.ts';

export const entity: EntityAdapter<Region> = createEntityAdapter<Region>({
  selectId: selectRegionUuid
});

// `state.entities` is keyed by id, so this is O(n); the previous `state.ids.includes` scan was O(n²)
// and dominated the load of farms with thousands of fields.
const onlyRegionsAlreadyInState = (state: RegionState, regions: Region[]): Region[] =>
  regions.filter(({ id }) => state.entities[id] !== undefined);

const initialState: RegionState = entity.getInitialState({
  isTableInfoLoading: true,
  areaVariablesPerSeason: {},
  isLoading: false,
  isLoadingGeometry: false,
  isCurrentInfoLoaded: false,
  isCurrentInfoLoading: false,
  isRegionsLoaded: false,
  error: null,
  choroplethLimits: {},
  mapLayersColorDictionary: {},
  latestWindowEvents: null,
  isLoadingEvents: false,
  isMethodologyLoaded: false,
  customVariables: [],
  areaVariables: [],
  isLoadingCustomVariables: false,
  isLoadingAreaVariables: false,
  isLoadingRegionData: false,
  regionData: [],
  isLoadingCoverageGroup: false,
  fieldsRepresentativeness: {},
  /** @deprecated this will be removed soon */
  seasonAreas: [],
  nextSprayCurrentInfoFields: [],
  useNewMapColors: false,
  currentInfoRegions: {},
  timelineMapHighlightEnabled: false,
  seasonFieldsCropsVarieties: null,
  recursiveRegionsAndSeasonFields: null,
  currentInfoCalculated: false
});

export default (state = initialState, action: ActionsResponses): RegionState => {
  switch (action.type) {
    case RegionActionsTypes.LOAD_REGION_METHODOLOGIES_SUCCESS:
      return entity.updateMany(setSeasonMethodologies(action.payload, state.entities), {
        ...state,
        isMethodologyLoaded: true
      });

    case RegionActionsTypes.LOAD_REGION_SEASON_FIELD: {
      return {
        ...state,
        isTableInfoLoading: true,
        areaVariableLoadingStage: AreaVariableMassiveStages.LOADING_SEASON_AREAS
      };
    }
    case CoreActionsTypes.SELECT_PROPERTY: {
      return entity.removeAll({
        ...state,
        isRegionsLoaded: false,
        currentInfoCalculated: false,
        error: null,
        latestScoutingScoresPerField: undefined
      });
    }
    case RegionActionsTypes.LOAD_REGION_SEASON_FIELD_FAILED:
    case RegionActionsTypes.LOAD_REGION_SEASON_FIELD_SUCCESS:
      return {
        ...state,
        isTableInfoLoading: false,
        recursiveRegionsAndSeasonFields: action.payload.areaInfo,
        seasonFieldsCropsVarieties: action.payload.seasonFieldsCropsVarieties,
        areaVariableLoadingStage: undefined
      };

    case RegionActionsTypes.GET_AREA_VARIABLES: {
      return {
        ...state,
        isTableInfoLoading: true,
        areaVariableLoadingStage: AreaVariableMassiveStages.LOADING_AREA_VARIABLES
      };
    }
    case RegionActionsTypes.EDIT_AREA_VARIABLES_MASSIVELY: {
      return {
        ...state,
        isTableInfoLoading: true,
        areaVariableLoadingStage: AreaVariableMassiveStages.EDITING_AREA_VARIABLES
      };
    }
    case RegionActionsTypes.DELETE_AREA_VARIABLE: {
      return {
        ...state,
        isTableInfoLoading: true,
        areaVariableLoadingStage: AreaVariableMassiveStages.DELETING_AREA_VARIABLES,
        isLoadingAreaVariables: true,
        error: null
      };
    }
    case RegionActionsTypes.CREATE_AREA_VARIABLES_MASSIVELY: {
      return {
        ...state,
        isTableInfoLoading: true,
        areaVariableLoadingStage: AreaVariableMassiveStages.CREATING_AREA_VARIABLES
      };
    }

    case RegionActionsTypes.EDIT_AREA_VARIABLES_MASSIVELY_FAILURE:
    case RegionActionsTypes.CREATE_AREA_VARIABLES_MASSIVELY_FAILURE:
    case RegionActionsTypes.EDIT_SEASON_AREA_DATES_MASSIVELY_FAILURE:
    case RegionActionsTypes.EDIT_SEASON_AREA_VARIETIES_MASSIVELY_FAILURE:
      return {
        ...state,
        isTableInfoLoading: false,
        areaVariableLoadingStage: undefined
      };

    case RegionActionsTypes.EDIT_SEASON_AREA_DATES_MASSIVELY: {
      return {
        ...state,
        isTableInfoLoading: true,
        areaVariableLoadingStage: AreaVariableMassiveStages.SAVING_DATES
      };
    }
    case RegionActionsTypes.EDIT_SEASON_AREA_VARIETIES_MASSIVELY: {
      return {
        ...state,
        isTableInfoLoading: true,
        areaVariableLoadingStage: AreaVariableMassiveStages.SAVING_VARIETIES
      };
    }
    case RegionActionsTypes.GET_AREA_VARIABLES_SUCCESS: {
      return {
        ...state,
        areaVariablesPerSeason: action.payload,
        isTableInfoLoading: false,
        areaVariableLoadingStage: undefined
      };
    }
    case RegionActionsTypes.UPDATE_REGION:
      return entity.updateOne(
        {
          id: action.payload.id,
          changes: {
            current_info: action.payload.current_info
          }
        },
        {
          ...state,
          isLoading: false,
          error: null
        }
      );
    case RegionActionsTypes.LOAD_REGION:
      return {
        ...state,
        isRegionsLoaded: false,
        isLoading: true,
        isCurrentInfoLoaded: false
      };
    case RegionActionsTypes.RELOAD_REGION:
      return {
        ...initialState,
        isRegionsLoaded: false,
        isLoading: true,
        isCurrentInfoLoaded: false,
        useNewMapColors: state.useNewMapColors,
        regionData: state.regionData
      };
    case RegionActionsTypes.LOAD_REGION_SUCCESS:
      return entity.addMany(action.payload, {
        ...state,
        isRegionsLoaded: true,
        isLoading: false,
        error: null
      });
    case RegionActionsTypes.RELOAD_REGION_DETAILED_INFO:
      return {
        ...state,
        isCurrentInfoLoaded: false,
        error: null
      };
    case RegionActionsTypes.LOAD_REGION_DETAILED_INFO:
    case RegionActionsTypes.LOAD_CURRENT_INFOS_BY_FIELD_IDS:
      return {
        ...state,
        isCurrentInfoLoaded: false,
        isCurrentInfoLoading: true,
        error: null
      };
    case RegionActionsTypes.LOAD_CURRENT_INFOS_BY_FIELDS_SUCCESS:
      return entity.upsertMany(action.payload, {
        ...state,
        isCurrentInfoLoaded: true,
        isCurrentInfoLoading: false,
        error: null
      });
    case RegionActionsTypes.LOAD_REGION_DETAILED_INFO_SUCCESS: {
      const currentRegionsFromPayload = onlyRegionsAlreadyInState(state, action.payload.regions);

      if (isEmpty(currentRegionsFromPayload)) return state;

      return entity.upsertMany(currentRegionsFromPayload, {
        ...state,
        isCurrentInfoLoaded: true,
        choroplethLimits: action.payload.choroplethLimits ?? state.choroplethLimits,
        mapLayersColorDictionary: action.payload.mapLayersColorDictionary,
        latestScoutingScoresPerField: action.payload.latestScoutingScoresPerField,
        error: null,
        currentInfoRegions: action.payload.currentInfoRegions
      });
    }
    case RegionActionsTypes.LOAD_REGION_DETAILED_INFO_FAILURE:
    case RegionActionsTypes.LOAD_CURRENT_INFOS_BY_FIELDS_FAILURE:
      return {
        ...state,
        isCurrentInfoLoaded: true,
        isCurrentInfoLoading: false,
        error: action.payload
      };
    case RegionActionsTypes.LOAD_REGION_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case RegionActionsTypes.REGION_ALREADY_LOADED:
      return {
        ...state,
        isLoading: false,
        isRegionsLoaded: true,
        isCurrentInfoLoaded: true,
        error: null
      };
    case RegionActionsTypes.LOAD_LATEST_WINDOW_EVENTS:
      return {
        ...state,
        isLoadingEvents: true,
        latestWindowEvents: null
      };
    case RegionActionsTypes.LOAD_LATEST_WINDOW_EVENTS_SUCCESS:
      return {
        ...state,
        isLoadingEvents: false,
        latestWindowEvents: action.payload
      };
    case RegionActionsTypes.LOAD_LATEST_WINDOW_EVENTS_FAILURE:
      return {
        ...state,
        isLoadingEvents: false,
        latestWindowEvents: null,
        error: action.payload
      };
    case RegionActionsTypes.UPDATE_REGION_NEMATODE_DAMAGE_SUCCESS:
      return entity.upsertMany(action.payload.regions, state);
    case RegionActionsTypes.TIMELINE_MAP_HIGHLIGHT_FILTER:
      return {
        ...state,
        timelineMapHighlightEnabled: !!action.payload.value
      };
    case RegionActionsTypes.TIMELINE_MAP_HIGHLIGHT_FILTER_SUCCESS:
      return entity.upsertMany(action.payload.regions, state);

    case RegionActionsTypes.CALCULATE_CURRENT_INFO: {
      return {
        ...state,
        currentInfoCalculated: false
      };
    }

    case RegionActionsTypes.CALCULATE_CURRENT_INFO_SUCCESS: {
      const currentRegionsFromPayload = onlyRegionsAlreadyInState(state, action.payload.regions);

      if (isEmpty(currentRegionsFromPayload)) return { ...state, currentInfoCalculated: true };

      return entity.upsertMany(currentRegionsFromPayload, {
        ...state,
        isCurrentInfoLoaded: true,
        currentInfoCalculated: true,
        choroplethLimits: action.payload.choroplethLimits ?? state.choroplethLimits,
        error: null
      });
    }

    case RegionActionsTypes.CANCEL_LOAD_WINDOW_EVENTS:
      return {
        ...state,
        isLoadingEvents: false,
        latestWindowEvents: null
      };
    case RegionActionsTypes.GET_AREA_VARIABLE:
      return {
        ...state,
        isLoadingAreaVariables: true,
        error: null
      };
    case RegionActionsTypes.GET_AREA_VARIABLE_SUCCESS:
      return {
        ...state,
        isLoadingAreaVariables: false,
        areaVariables: action.payload,
        error: null
      };
    case RegionActionsTypes.GET_AREA_VARIABLE_FAILURE:
      return {
        ...state,
        isLoadingAreaVariables: false,
        error: action.payload
      };
    case RegionActionsTypes.DELETE_AREA_VARIABLE_FAILURE:
      return {
        ...state,
        isLoadingAreaVariables: false,
        error: action.payload,
        isTableInfoLoading: false,
        areaVariableLoadingStage: undefined
      };
    case RegionActionsTypes.GET_REGION_DATA:
      return {
        ...state,
        isLoadingRegionData: true,
        error: null
      };
    case RegionActionsTypes.GET_REGION_DATA_SUCCESS:
      return {
        ...state,
        isLoadingRegionData: false,
        regionData: [...state.regionData, ...action.payload]
      };
    case RegionActionsTypes.CLEAR_REGION_DATA:
      return {
        ...state,
        isLoadingRegionData: false,
        regionData: initialState.regionData
      };

    case RegionActionsTypes.GET_AREA_DAY_REPRESENTATIVENESS:
      return {
        ...state,
        isLoadingCoverageGroup: true
      };

    case RegionActionsTypes.GET_AREA_DAY_REPRESENTATIVENESS_SUCCESS:
      return {
        ...state,
        isLoadingCoverageGroup: false,
        fieldsRepresentativeness: {
          ...state.fieldsRepresentativeness,
          [action.payload.id]: [...(state.fieldsRepresentativeness[action.payload.id] || []), action.payload]
        }
      };

    case PropertyActionsTypes.LOAD_SEVERITY_BY_INDICATOR_SUCCESS: {
      const hasPressureIndicator: boolean | undefined = verifyPressureIndicator(action);
      const indicatorPressureResults = action.payload.data;
      const indicatorPressureResultMap = createDictionaryFromList(indicatorPressureResults, 'area_id');
      const updatedChildRegions = getUpdatedChildRegionsWithIndicatorPressure(
        state.entities,
        indicatorPressureResultMap,
        state.useNewMapColors
      );
      const updatedParentRegions = getUpdatedParentRegionsWithIndicatorPressure(state.entities, updatedChildRegions);
      return entity.updateMany([...updatedChildRegions, ...updatedParentRegions].map(mapRegionToRegionUpdate), {
        ...state,
        severityIndicatorSelected: action.payload.indicator,
        hasPressureIndicator
      });
    }
    case PropertyActionsTypes.CLEAR_INDICATOR_SEVERITY: {
      const updatedChildRegions = getUpdatedChildRegionsWithIndicatorPressure(state.entities, {});
      const updatedParentRegions = getUpdatedParentRegionsWithIndicatorPressure(state.entities, updatedChildRegions);
      return entity.updateMany([...updatedChildRegions, ...updatedParentRegions].map(mapRegionToRegionUpdate), {
        ...state,
        severityIndicatorSelected: undefined,
        hasPressureIndicator: true
      });
    }

    case PropertyActionsTypes.SET_USE_NEW_MAP_COLORS_FLAG: {
      return {
        ...state,
        useNewMapColors: action.payload
      };
    }
    default:
      return state;
  }
};
